import type { ChangeEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, type DropResult } from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { AlertBanner } from '@shipstation/client-common/components/alert-banner';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexRow } from 'ui-components/components/flex/row';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { v4 } from 'uuid';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import { ErrorBoundary } from '../../../components/error-boundary';
import {
  MULTI_PACKAGE_APPLY_PACKAGE_SET_CLICKED,
  MULTI_PACKAGE_SAVE_PACKAGES_CLICKED,
  MULTI_PACKAGE_SAVE_WITH_PACKAGE_SET,
} from '../../../constants/analytics-events';
import { useMultiPackageShipments } from '../../../feature-flags/hooks/use-multi-package-shipments';
import { Segment } from '../../../services/analytics-services/segment';
import Global from '../../../store/global-selectors';
import { LogService } from '../../../services/log-service';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import {
  useMultiPackageEmptyPackageWarningDismissed,
  useMultiPackageUnassignedItemsWarningDismissed,
} from '../../../hooks/use-user-setting';
import { saveUserSetting } from '../../../store/user/user.actions';
import { FulfillmentPlannerContext, useFulfillmentPlannerContext } from '../../../fulfillment-planner/context';
import { setMultiPackageDrawerOpen } from '../../../orders/order-details/configure-shipment/fields/packing-recommend/use-packing-applied';
import { ModalPortal } from '../../modal-portal';
import { useSavePackageSet } from '../multi-package-modal/hooks/use-save-package-set';
import { setupV2PackageSet, setupV3PackageSet } from '../multi-package-modal/setup-package-set';
import { PackageConfigAlertModal } from './components/package-config-alert-modal/package-config-alert-modal';
import { OrderItemsPanel } from './components/order-items-panel/order-items-panel';
import { PackagesPanel } from './components/packages-panel/packages-panel';
import { SummaryPanel } from './components/summary-panel/summary-panel';
import { DRAGGABLE_ID_SEPARATOR, PACKAGE_ID_PREFIX, UNASSIGNED_ITEMS_ID } from './constants/drag-drop';
import { MultiPackageModalProvider } from './contexts/multi-package-modal-context';
import {
  convertToAssignments,
  getItemsAssignedCount,
  hasUnassignedItems,
  assignUnassignedToFirstPackage,
} from './helpers/assignment-helpers';
import { createInitialPackages, hasEmptyPackages, areTargetPackagesBlank } from './helpers/package-helpers';
import { useItemAssignments } from './hooks/use-item-assignments';
import { usePackageOperations } from './hooks/use-package-operations';
import { messages } from './messages';
import style from './multi-package-modal-dnd.module.css';

import type { PackageSet } from '@shipstation/client-common/types/seller-data';
import type { Package, OrderItem } from './types';

const MODAL_CONTEXTS = {
  Orders: 'orders',
  Settings: 'settings',
} as const;

type MultiPackageModalDnDProps = {
  hasInsuranceProviderSelected?: boolean;
  initialPackages?: Partial<Package>[];
  onSave?: (packages: Package[]) => void;
  shouldRequirePackageDescription?: boolean;
  hasMultiPackageSupport?: boolean;
  context?: (typeof MODAL_CONTEXTS)[keyof typeof MODAL_CONTEXTS];
  items?: OrderItem[];
  fulfillmentPlanId?: string;
  suppressExtraEmptyPackage?: boolean;
};

export const MultiPackageModalDnD = ({
  hasInsuranceProviderSelected = false,
  initialPackages = [],
  onSave,
  shouldRequirePackageDescription = false,
  hasMultiPackageSupport = false,
  context = MODAL_CONTEXTS.Orders,
  items = [],
  fulfillmentPlanId = '',
  suppressExtraEmptyPackage = false,
}: MultiPackageModalDnDProps) => {
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionsUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const packageSetsList = useSelector((state) => Global.selectSellerPackageSets(state)) || [];
  const allPackages = useSelector(Global.selectSellerPackages);
  const { formatMessage } = useIntl();
  const enableMultiPackageShipments = useMultiPackageShipments();

  const customPackages = useMemo(
    () => allPackages.filter((pkg) => pkg.isCustom && !pkg.isHidden && pkg.dimensions !== null),
    [allPackages],
  );

  const isOrdersContext = context === MODAL_CONTEXTS.Orders;
  const shouldShowMultiPackageNotSupportedBanner = isOrdersContext && enableMultiPackageShipments && !hasMultiPackageSupport;

  const {
    onPackageSetNameChange,
    onSavePackageSet,
    packageSetName,
    shouldSaveNewPackageSet,
    toggleShouldSaveNewPackageSet,
    setShouldSaveNewPackageSet,
  } = useSavePackageSet();

  const [packages, setPackages] = useState(() => {
    return createInitialPackages(
      initialPackages,
      sellerDefaultCurrencyCode,
      sellerDefaultDimensionsUnit,
      sellerDefaultWeightUnit,
      hasInsuranceProviderSelected,
      suppressExtraEmptyPackage,
    );
  });

  const [expandedPackageIndex, setExpandedPackageIndex] = useState<number | null>(null);
  const dispatch = useDispatch();
  const isUnassignedItemsWarningDismissed = useMultiPackageUnassignedItemsWarningDismissed();
  const isEmptyPackageWarningDismissed = useMultiPackageEmptyPackageWarningDismissed();
  const [showEmptyPackageConfirmation, setShowEmptyPackageConfirmation] = useState(false);
  const [showUnassignedItemsConfirmation, setShowUnassignedItemsConfirmation] = useState(false);
  const resolvedQuantitiesRef = React.useRef<ReturnType<typeof assignUnassignedToFirstPackage> | null>(null);

  const {
    itemQuantities,
    assignItemToPackage,
    unassignItemFromPackage,
    moveItemBetweenPackages,
    updateChunkQuantity,
    reindexPackagesAfterRemoval,
    resetAssignments,
  } = useItemAssignments(items, packages);

  const {
    addPackages,
    duplicatePackage,
    removePackage,
    changePackageDimensions,
    changeWeight,
    changePackageContentDescription,
    changeInsuranceValue,
    applyCustomPackageToPackage,
  } = usePackageOperations({
    packages,
    setPackages,
    itemQuantities,
    unassignItemFromPackage,
    reindexPackagesAfterRemoval,
    sellerDefaultWeightUnit,
    sellerDefaultDimensionsUnit,
    sellerDefaultCurrencyCode,
  });

  const ambientFulfillmentPlannerContext = useFulfillmentPlannerContext();

  useEffect(() => {
    if (!fulfillmentPlanId) return;
    setMultiPackageDrawerOpen(fulfillmentPlanId, true);
    return () => setMultiPackageDrawerOpen(fulfillmentPlanId, false);
  }, [fulfillmentPlanId]);

  const handleRecommendationApply = useCallback(
    (recommendedPackages: Package[]) => {
      const packagesWithIds = recommendedPackages.map((pkg) => ({ ...pkg, id: pkg.id ?? v4() }));
      setPackages(packagesWithIds);
      resetAssignments(items, packagesWithIds);
    },
    [items, resetAssignments],
  );

  const packingRecommendContextValue = useMemo(
    () => ({
      ...ambientFulfillmentPlannerContext,
      onMultiPackageSave: handleRecommendationApply as (...args: unknown[]) => void,
    }),
    [ambientFulfillmentPlannerContext, handleRecommendationApply],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const { source, destination, draggableId } = result;
      const [itemId, chunkId] = draggableId.split(DRAGGABLE_ID_SEPARATOR);

      if (source.droppableId === UNASSIGNED_ITEMS_ID && destination.droppableId.startsWith(PACKAGE_ID_PREFIX)) {
        const packageIndex = parseInt(destination.droppableId.split('-')[1], 10);
        assignItemToPackage(itemId, chunkId, packageIndex, destination.index);
      }

      if (source.droppableId.startsWith(PACKAGE_ID_PREFIX) && destination.droppableId === UNASSIGNED_ITEMS_ID) {
        unassignItemFromPackage(itemId, chunkId);
      }

      if (source.droppableId.startsWith(PACKAGE_ID_PREFIX) && destination.droppableId.startsWith(PACKAGE_ID_PREFIX)) {
        const toPackage = parseInt(destination.droppableId.split('-')[1], 10);
        moveItemBetweenPackages(itemId, chunkId, toPackage, destination.index);
      }
    },
    [assignItemToPackage, unassignItemFromPackage, moveItemBetweenPackages],
  );

  const applyPackageSetFromList = useCallback(
    (packageSet: PackageSet) => {
      Segment.trackEvent(MULTI_PACKAGE_APPLY_PACKAGE_SET_CLICKED, {
        packageSetName: packageSet.name,
      });

      const jsonData = packageSet.jsonData || '';
      if (!jsonData) {
        return;
      }

      try {
        const parsedData = JSON.parse(jsonData);
        const packagesWithUnits = Array.isArray(parsedData)
          ? setupV2PackageSet(parsedData, sellerDefaultDimensionsUnit)
          : setupV3PackageSet(parsedData, sellerDefaultDimensionsUnit);

        if (!packagesWithUnits || packagesWithUnits.length === 0) {
          return;
        }

        const packagesWithIds = packagesWithUnits.map((pkg) => {
          const basePackage = {
            ...pkg,
            id: v4(),
          };

          // insuredValue might not exist in V2 packages, so we ensure it's added
          if (!('insuredValue' in basePackage)) {
            return {
              ...basePackage,
              insuredValue: {
                value: 0,
                code: packages[0]?.insuredValue?.code ?? sellerDefaultCurrencyCode,
              },
            };
          }

          return {
            ...basePackage,
            insuredValue: basePackage.insuredValue ?? {
              value: 0,
              code: packages[0]?.insuredValue?.code ?? sellerDefaultCurrencyCode,
            },
          };
        }) as Package[];

        const numberOfPackages = packagesWithIds.length;
        if (areTargetPackagesBlank(packages, numberOfPackages)) {
          setPackages([...packagesWithIds, ...packages.slice(numberOfPackages)]);
        } else {
          setPackages([...packages, ...packagesWithIds]);
        }
      } catch (error) {
        LogService.logError(error);
      }
    },
    [sellerDefaultDimensionsUnit, packages, sellerDefaultCurrencyCode],
  );

  const performSave = useCallback(() => {
    const effectiveQuantities = resolvedQuantitiesRef.current ?? itemQuantities;
    const itemsAssignedCount = getItemsAssignedCount(effectiveQuantities);
    const packagesWithAssignments = convertToAssignments(effectiveQuantities, packages);

    const parsedPackages = packagesWithAssignments.map((pack) => {
      const { id: _id, ...newPack } = pack;
      return newPack;
    });

    if (shouldSaveNewPackageSet) {
      Segment.trackEvent(MULTI_PACKAGE_SAVE_WITH_PACKAGE_SET, {
        context: 'orders',
        packageSetName,
      });
    } else {
      Segment.trackEvent(MULTI_PACKAGE_SAVE_PACKAGES_CLICKED, {
        context: 'orders',
        packageCount: packagesWithAssignments.length,
        itemsAssignedCount,
      });
    }

    if (onSave) {
      onSave(packagesWithAssignments);
    }

    onSavePackageSet(parsedPackages);
    setShowEmptyPackageConfirmation(false);
    setShowUnassignedItemsConfirmation(false);
    resolvedQuantitiesRef.current = null;
  }, [packages, itemQuantities, onSave, onSavePackageSet, shouldSaveNewPackageSet, packageSetName]);

  const dismissWarningPermanently = useCallback(
    (setting: { settingName: string }) => {
      dispatch(saveUserSetting({ settingName: setting.settingName, settingValue: true, excludeV3prefix: true }));
    },
    [dispatch],
  );

  const handleConfirmUnassignedItems = useCallback(
    (shouldHideInFuture: boolean) => {
      if (shouldHideInFuture) {
        dismissWarningPermanently(UserSettings.multiPackageUnassignedItemsWarningDismissed);
      }
      resolvedQuantitiesRef.current = assignUnassignedToFirstPackage(itemQuantities);
      performSave();
    },
    [itemQuantities, performSave, dismissWarningPermanently],
  );

  const handleConfirmEmptyPackages = useCallback(
    (shouldHideInFuture: boolean) => {
      if (shouldHideInFuture) {
        dismissWarningPermanently(UserSettings.multiPackageEmptyPackageWarningDismissed);
      }
      performSave();
    },
    [performSave, dismissWarningPermanently],
  );

  const onSaveClicked = useCallback(() => {
    const hasNoItemsAllocated = items.length > 0 && getItemsAssignedCount(itemQuantities) === 0;
    if (hasNoItemsAllocated) {
      performSave();
      return;
    }

    resolvedQuantitiesRef.current = null;

    if (items.length > 0 && hasUnassignedItems(items, itemQuantities)) {
      if (!isUnassignedItemsWarningDismissed) {
        setShowUnassignedItemsConfirmation(true);
        return;
      }
      resolvedQuantitiesRef.current = assignUnassignedToFirstPackage(itemQuantities);
    }

    const effectiveQuantities = resolvedQuantitiesRef.current ?? itemQuantities;
    if (hasEmptyPackages(packages, effectiveQuantities) && !isEmptyPackageWarningDismissed) {
      setShowEmptyPackageConfirmation(true);
      return;
    }

    performSave();
  }, [items, packages, itemQuantities, performSave, isUnassignedItemsWarningDismissed, isEmptyPackageWarningDismissed]);

  const handlePackageSetNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setShouldSaveNewPackageSet?.(true);
      onPackageSetNameChange(e);
    },
    [setShouldSaveNewPackageSet, onPackageSetNameChange],
  );

  const handleOnToggleExpand = useCallback((index: number) => {
    setExpandedPackageIndex((prev) => (prev === index ? null : index));
  }, []);

  const modalHeaderId = 'multi-package-modal-header';

  return (
    <ErrorBoundary errorMessageId='multiPackageModal'>
      <Modal className={style.modal} modalHeaderId={modalHeaderId}>
        <ModalHeader id={modalHeaderId}>
          <FormattedMessage {...messages.multiPackageTitle} />
        </ModalHeader>

        <FulfillmentPlannerContext.Provider value={packingRecommendContextValue}>
          <MultiPackageModalProvider
            value={{
              fulfillmentPlanId,
              packages,
              hasInsuranceProviderSelected,
              shouldRequirePackageDescription,
              customPackages,
              items,
              itemQuantities,
              changePackageDimensions,
              changeWeight,
              changePackageContentDescription,
              changeInsuranceValue,
              applyCustomPackageToPackage,
              unassignItemFromPackage,
              updateChunkQuantity,
            }}
          >
            <ModalBody className={style.body}>
              <DragDropContext onDragEnd={onDragEnd}>
                <div className={style['left-column']}>
                  <OrderItemsPanel />
                  <SummaryPanel packages={packages} />
                </div>

                <div className={style['right-column']}>
                  {shouldShowMultiPackageNotSupportedBanner && (
                    <AlertBanner description={<FormattedMessage {...messages.multiPackageNotSupported} />} fullWidth status='warning' />
                  )}
                  <PackagesPanel
                    packages={packages}
                    expandedPackageIndex={expandedPackageIndex}
                    onToggleExpand={handleOnToggleExpand}
                    onAddPackages={addPackages}
                    onDuplicatePackage={duplicatePackage}
                    onRemovePackage={removePackage}
                    packageSetsList={packageSetsList}
                    applyPackageSetFromList={applyPackageSetFromList}
                  />

                  <div className={style['right-footer']}>
                    <div className={style['new-packages']}>
                      <Checkbox
                        checked={shouldSaveNewPackageSet}
                        label={formatMessage(messages.saveNewPackageSet)}
                        onChange={toggleShouldSaveNewPackageSet}
                      />
                      {shouldSaveNewPackageSet && (
                        <input
                          className={style['package-set-name']}
                          onChange={handlePackageSetNameChange}
                          type='text'
                          value={packageSetName}
                          placeholder={formatMessage(messages.packageSetNameLabel)}
                        />
                      )}
                    </div>
                    <FlexRow className={style['footer-buttons']}>
                      <ModalFooterCancelButton />
                      <Button onClick={onSaveClicked} theme='primary'>
                        <FormattedMessage {...messages.savePackagesButton} />
                      </Button>
                    </FlexRow>
                  </div>
                </div>
              </DragDropContext>
            </ModalBody>
          </MultiPackageModalProvider>
        </FulfillmentPlannerContext.Provider>
      </Modal>

      {showUnassignedItemsConfirmation && (
        <ModalPortal onClose={() => setShowUnassignedItemsConfirmation(false)}>
          <PackageConfigAlertModal
            heading={messages.unassignedItemsModalHeading}
            message={messages.unassignedItemsModalMessage}
            onConfirm={handleConfirmUnassignedItems}
            onCancel={() => setShowUnassignedItemsConfirmation(false)}
          />
        </ModalPortal>
      )}

      {showEmptyPackageConfirmation && (
        <ModalPortal onClose={() => setShowEmptyPackageConfirmation(false)}>
          <PackageConfigAlertModal
            heading={messages.emptyPackageModalHeading}
            message={messages.emptyPackageModalMessage}
            onConfirm={handleConfirmEmptyPackages}
            onCancel={() => setShowEmptyPackageConfirmation(false)}
          />
        </ModalPortal>
      )}
    </ErrorBoundary>
  );
};
